import React from 'react';
import PropTypes from 'prop-types';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  IconButton,
  Typography,
  Box,
  Button,
  Avatar
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import ChatIcon from '@mui/icons-material/Chat';
import { getWelcomeMessage } from '../customWelcomeMessage';
import { getAgentForTask } from '../../../utils/agentNames';

/**
 * AI Agent Dialog Component
 *
 * Dialog that displays when a user clicks on a task in the PRDflow Foundation page
 * Shows a centered avatar with a welcome message and interaction options
 *
 * @param {Object} props - Component props
 * @param {boolean} props.open - Whether the dialog is open
 * @param {function} props.onClose - Function to call when dialog should close
 * @param {Object} props.task - The task that was clicked
 * @param {Object} props.user - Current user information
 * @param {string} props.projectId - Current project ID
 * @param {Object} props.sectionAvatars - Map of section IDs to their avatar data
 * @returns {React.ReactElement} Dialog component
 */
const AgentDialog = ({
  open,
  onClose,
  task = {},
  user = {},
  projectId,
  sectionAvatars = {}
}) => {
  // Direct mapping of task titles to agent names
  const getAgentByTaskTitle = (title) => {
    if (!title) return null;

    const normalizedTitle = title.toLowerCase().trim();

    const agentMap = {
      'project info': { name: 'Proja', role: 'Project Info' },
      'features': { name: 'Featy', role: 'Features' },
      'user roles': { name: 'Rolie', role: 'User Roles' },
      'user stories': { name: 'Storia', role: 'User Stories' },
      'process flows': { name: 'Flo', role: 'Process Flows' },
      'knowledge hub analysis': { name: 'Felix', role: 'Analysis Orchestrator' },

      'ui/ux references': { name: 'Refa', role: 'UI/UX References' },
      'ui/ux reference': { name: 'Refa', role: 'UI/UX References' },
      'wireframes': { name: 'Wiro', role: 'UI/UX Wireframes' },
      'ui/ux wireframes': { name: 'Wiro', role: 'UI/UX Wireframes' },
      'design guidelines': { name: 'Guy', role: 'UI/UX Guidelines' },
      'ui/ux guidelines': { name: 'Guy', role: 'UI/UX Guidelines' },
      'design system': { name: 'Stylia', role: 'UI/UX Style Guide' },
      'ui/ux style guide': { name: 'Stylia', role: 'UI/UX Style Guide' },
      'designs': { name: 'Layo', role: 'UI/UX Layout' },
      'ui/ux layout': { name: 'Layo', role: 'UI/UX Layout' },
      'knowledge hub ui/ux': { name: 'Diana', role: 'UI/UX Orchestrator' },

      'field validations': { name: 'Fieldy', role: 'Field Validations' },
      'technology stack': { name: 'Stacky', role: 'Technology Stack' },
      'tech stack': { name: 'Stacky', role: 'Technology Stack' },
      'architecture': { name: 'Archie', role: 'Architecture' },
      'development guidelines': { name: 'Cody', role: 'Coding Guidelines' },
      'coding guidelines': { name: 'Cody', role: 'Coding Guidelines' },
      'dev guidelines': { name: 'Cody', role: 'Coding Guidelines' },
      'security guidelines': { name: 'Secury', role: 'Security Guidelines' },
      'user permissions': { name: 'Missy', role: 'User Permissions' },
      'knowledge hub coding': { name: 'Devon', role: 'Development Orchestrator' },
      'integration': { name: 'Integrity', role: 'Integration Specialist' },

      'functional testing': { name: 'Funcy', role: 'Functional Testing' },
      'automation testing': { name: 'Auton', role: 'Automation Testing' },
      'security testing': { name: 'Secu', role: 'Security Testing' },
      'performance testing': { name: 'Perfie', role: 'Performance Testing' },
      'knowledge hub quality': { name: 'Tessa', role: 'Quality Orchestrator' },

      'code version control': { name: 'Versio', role: 'Code Version Control' },
      'version control': { name: 'Versio', role: 'Code Version Control' },
      'ci/cd pipeline': { name: 'Liny', role: 'CI/CD Pipeline' },
      'deployment': { name: 'Deplo', role: 'Deployment' },
      'knowledge hub infrastructure': { name: 'Ian', role: 'Infrastructure Orchestrator' }
    };

    return agentMap[normalizedTitle] || null;
  };

  // First try direct mapping by task title
  const directAgent = task?.title ? getAgentByTaskTitle(task.title) : null;

  // If direct mapping fails, fall back to agent service
  const agentInfo = directAgent || getAgentForTask(task);

  // Get agent info for the task from our central agent system
  const taskAgent = agentInfo || { name: 'Ailyce', role: 'AI Agent' };

  // Get the avatar URL from the pre-fetched sectionAvatars map
  const sectionId = task?.sectionId || (task?.section?._id || task?.section);
  const sectionAvatar = sectionId ? sectionAvatars[sectionId] : null;
  const avatarImage = sectionAvatar?.url || null;

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 2,
          maxHeight: '90vh',
          overflow: 'hidden',
          position: 'relative',
          top: '5vh',
          margin: '0 auto'
        }
      }}
    >
      <DialogTitle
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          pb: 2,
          pt: 2,
          px: 3,
          borderBottom: '1px solid rgba(0, 0, 0, 0.1)'
        }}
      >
        <Typography variant="h6" fontWeight={600}>
          {task?.title || 'AI Assistant'}
        </Typography>
        <IconButton onClick={onClose} size="small" aria-label="close">
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={{ p: 0 }}>
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            textAlign: 'center',
            py: 8,
            px: 4
          }}
        >
          <Avatar
            src={avatarImage}
            alt={taskAgent.name}
            sx={{
              width: 120,
              height: 120,
              boxShadow: '0 4px 12px rgba(0, 0, 0, 0.1)',
              mb: 3,
              bgcolor: 'transparent',
              border: '2px solid #ffcccc',
              fontSize: '3rem',
              color: '#ff6666',
              '& > img': {
                objectFit: 'cover',
                width: '100%',
                height: '100%'
              }
            }}
          >
            {taskAgent.name.charAt(0).toUpperCase()}
          </Avatar>

          <Typography variant="body1" color="text.secondary" sx={{ mb: 4, maxWidth: '450px' }}>
            {getWelcomeMessage(
              user?.firstName || user?.name || user?.username || 'there',
              taskAgent.name,
              task?.title || 'topic'
            ).split('\n\n').map((paragraph, index) => (
              <React.Fragment key={index}>
                {paragraph}
                {index < 2 && <><br /><br /></>}
              </React.Fragment>
            ))}
          </Typography>

          <Box sx={{
            display: 'flex',
            justifyContent: 'center',
            mt: 2
          }}>
            <Button
              variant="contained"
              startIcon={<ChatIcon />}
              sx={{
                textTransform: 'none',
                minWidth: '160px',
                bgcolor: '#485869',
                '&:hover': {
                  bgcolor: '#3a4a59',
                }
              }}
              onClick={() => {
                const taskId = task?.id || task?._id || task?.taskId || task?.task_id || task?.sectionId;

                if (projectId && taskId) {
                  window.location.href = `/projects/${projectId}/conversation?task=${taskId}`;
                } else {
                  console.error('Cannot navigate: missing project ID or task ID');
                }
              }}
            >
              Start Chat
            </Button>
          </Box>
        </Box>
      </DialogContent>
    </Dialog>
  );
};

AgentDialog.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  task: PropTypes.object,
  user: PropTypes.object,
  projectId: PropTypes.string,
  sectionAvatars: PropTypes.object
};

export default AgentDialog;
